import { useEffect, useMemo, useRef } from 'react'
import { Select } from '@/components/ui'
import { useSignatures } from '../api/queries'

export interface SignaturePickerProps {
  /** Backend route returning `{ signatures: Signature[] }`. `null` = idle. */
  endpoint: string | null
  /** Called with the chosen signature's HTML (empty string = none). */
  onApply: (signatureHtml: string) => void
  /** Apply the mailbox's default signature once, on first load. */
  applyDefaultOnLoad?: boolean
}

/**
 * Dropdown of the mailbox's shared MailDesk signatures. Choosing one hands its
 * HTML up via `onApply` — the work-view injects it into the draft body so the
 * operator sees exactly what will be sent (matching how MailDesk composes).
 */
export function SignaturePicker({
  endpoint,
  onApply,
  applyDefaultOnLoad = false,
}: SignaturePickerProps) {
  const { data } = useSignatures(endpoint)
  const sigs = useMemo(() => data?.signatures ?? [], [data])
  const appliedDefault = useRef(false)

  useEffect(() => {
    if (!applyDefaultOnLoad || appliedDefault.current || sigs.length === 0) return
    // Prefer the flagged default; otherwise fall back to the first shared
    // signature. Mailboxes often have exactly one shared signature with no
    // is_default flag set (the Orders mailbox does) — without this fallback
    // the composer would send bare even though a signature is available.
    const chosen = sigs.find((s) => s.is_default) ?? sigs[0]
    if (chosen) {
      appliedDefault.current = true
      onApply(chosen.body_html)
    }
  }, [applyDefaultOnLoad, sigs, onApply])

  if (sigs.length === 0) return null

  return (
    <Select
      aria-label="Signature"
      defaultValue=""
      onChange={(e) => {
        const id = e.target.value
        if (!id) {
          onApply('')
          return
        }
        const sig = sigs.find((s) => String(s.id) === id)
        if (sig) onApply(sig.body_html)
      }}
    >
      <option value="">No signature</option>
      {sigs.map((s) => (
        <option key={s.id} value={String(s.id)}>
          {s.name}
          {s.is_default ? ' (default)' : ''}
        </option>
      ))}
    </Select>
  )
}
